import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { meetingsAPI, peoplePlatformAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';

const MeetingMinuteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error } = useToast();
  const { hasPermission } = usePermissions();
  const [meeting, setMeeting] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersMap, setMembersMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMeetingDetails();
  }, [id]);

  const loadMeetingDetails = async () => {
    try {
      setLoading(true);
      const [meetingResponse, membersResponse] = await Promise.all([
        meetingsAPI.getById(id),
        peoplePlatformAPI.list({ limit: 2000, skip: 0 })
      ]);
      
      // Vérifier que la réponse contient bien des données
      if (!meetingResponse || !meetingResponse.data) {
        throw new Error('Réponse invalide du serveur');
      }
      
      setMeeting(meetingResponse.data);
      
      const membersList = mapPeopleListResponse(membersResponse.data);
      
      setMembers(membersList);
      
      // Créer un map des membres par ID
      const map = {};
      membersList.forEach(member => {
        map[member.id] = member.name;
      });
      setMembersMap(map);
    } catch (err) {
      console.error('Failed to load meeting details:', err);
      console.error('Error details:', err.response?.data || err.message);
      error(err.response?.data?.detail || err.message || 'Erreur lors du chargement du procès-verbal');
      navigate('/meetings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Procès-verbal introuvable</p>
          <button
            onClick={() => navigate('/meetings')}
            className="mt-4 px-4 py-2.5 min-h-[44px] bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy text-sm md:text-base"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const meetingDate = new Date(meeting.date);
  const isPublished = meeting.is_published;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Sticky on mobile */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 shadow-sm">
        <Link
          to="/meetings"
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm md:text-base font-medium">Retour</span>
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-3 py-4 md:px-6 md:py-8">
        {/* Meeting Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 md:mb-6 overflow-hidden">
          <div className={`h-1.5 md:h-2 ${isPublished ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}></div>
          <div className="p-4 md:p-8">
            <div className="flex items-start justify-between gap-3 md:gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-xl flex-shrink-0 ${
                    isPublished 
                      ? 'bg-green-100' 
                      : meeting.pending_approval
                      ? 'bg-blue-100'
                      : 'bg-amber-100'
                  }`}>
                    <svg className={`h-5 w-5 md:h-6 md:w-6 ${
                      isPublished 
                        ? 'text-green-600' 
                        : meeting.pending_approval
                        ? 'text-blue-600'
                        : 'text-amber-600'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 md:mb-3 break-words">{meeting.title}</h1>
                    <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold ${
                        isPublished 
                          ? 'bg-green-100 text-green-700' 
                          : meeting.pending_approval
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {isPublished ? (
                          <>✓ Publié</>
                        ) : meeting.pending_approval ? (
                          <>⏱ En attente</>
                        ) : (
                          <>📝 Brouillon</>
                        )}
                      </span>
                      <span className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-600">
                        <svg className="h-4 w-4 md:h-5 md:w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="hidden sm:inline">{formatDate(meeting.date)}</span>
                        <span className="sm:hidden">{meetingDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                      </span>
                      {meeting.location && (
                        <span className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-600">
                          <svg className="h-4 w-4 md:h-5 md:w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="truncate max-w-[150px] md:max-w-none">{meeting.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Ordre du jour */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <div className="p-1.5 md:p-2 bg-entrea-panel rounded-lg">
                <svg className="h-4 w-4 md:h-5 md:w-5 text-entrea-royal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h2 className="text-base md:text-xl font-bold text-gray-900">Ordre du jour</h2>
            </div>
            <ul className="space-y-2 md:space-y-3">
              {meeting.agenda && meeting.agenda.length > 0 ? (
                meeting.agenda.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-700">
                    <span className="text-entrea-royal mt-0.5 md:mt-1 font-bold">•</span>
                    <span className="flex-1">{item}</span>
                  </li>
                ))
              ) : (
                <li className="text-gray-400 italic">Aucun point à l'ordre du jour</li>
              )}
            </ul>
          </div>

          {/* Participants */}
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <div className="p-1.5 md:p-2 bg-entrea-panel rounded-lg">
                <svg className="h-4 w-4 md:h-5 md:w-5 text-entrea-ink" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h2 className="text-base md:text-xl font-bold text-gray-900">Participants</h2>
              <span className="ml-auto text-xs md:text-sm text-gray-500">({meeting.participants?.length || 0})</span>
            </div>
            <ul className="space-y-2 max-h-64 md:max-h-96 overflow-y-auto">
              {(() => {
                const participantsToDisplay = meeting.participants_display || 
                  (meeting.participants && meeting.participants.length > 0
                    ? meeting.participants.map(p => {
                        if (typeof p === 'string' && !p.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                          return p;
                        }
                        return membersMap[p] || (typeof p === 'string' ? `Membre (${p.substring(0, 8)}...)` : 'Inconnu');
                      })
                    : []);
                
                return participantsToDisplay.length > 0 ? (
                  participantsToDisplay.map((participantName, idx) => {
                    const initial = (participantName || '').charAt(0).toUpperCase() || '?';
                    return (
                      <li key={idx} className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-gray-700">
                        <div className="h-7 w-7 md:h-8 md:w-8 rounded-full bg-entrea-panel flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-entrea-ink">{initial}</span>
                        </div>
                        <span className="truncate">{participantName}</span>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-gray-400 italic">Aucun participant</li>
                );
              })()}
            </ul>
          </div>
        </div>

        {/* Decisions */}
        <div className="mt-4 md:mt-6 bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-200">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <div className="p-1.5 md:p-2 bg-emerald-100 rounded-lg">
              <svg className="h-4 w-4 md:h-5 md:w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-base md:text-xl font-bold text-gray-900">Décisions</h2>
          </div>
          <ul className="space-y-2 md:space-y-3">
            {meeting.decisions && meeting.decisions.length > 0 ? (
              meeting.decisions.map((decision, idx) => (
                <li key={idx} className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-700">
                  <span className="text-emerald-600 mt-0.5 md:mt-1 font-bold">✓</span>
                  <span className="flex-1">{decision}</span>
                </li>
              ))
            ) : (
              <li className="text-gray-400 italic">Aucune décision prise</li>
            )}
          </ul>
        </div>

      </div>
    </div>
  );
};

export default MeetingMinuteDetails;



